import { useCallback, useRef, useState } from "react"
import { default as ky, KyResponse, HTTPError, TimeoutError } from "ky"
import { GetErrorName } from "@/lib/errors"
import { redirectToLoginForExpiredSession } from "@/lib/authRedirect"

export interface UrlBuilderParams {
  [key: string]: string | number | boolean
}

export interface FetchFnParam<T, K> {
  headers?: Record<string, string | undefined>
  body?: BodyInit | null
  jsonBody?: K
  cache?: RequestCache
  signal?: AbortSignal
  searchParams?: URLSearchParams
  urlBuilderParams?: UrlBuilderParams
  callbackFn?: (data?: T) => void
  preserveData?: boolean
  retry?: number
  timeout?: number | false
}

export const enum FetchMethod {
  Get = "GET",
  Post = "POST",
  Put = "PUT",
  Patch = "PATCH",
  Delete = "DELETE",
}

export const enum FetchStatus {
  Idle = "idle",
  Loading = "loading",
  Success = "success",
  Error = "error",
}

export interface FetchResult<T, K = Record<string, string | number | boolean>> {
  status: FetchStatus
  error: unknown | undefined
  response?: KyResponse
  data?: T
  fetchFn: (params?: FetchFnParam<T, K>) => Promise<T | undefined>
}

export function useFetch<
  T,
  K = Record<string, string | number | boolean | object>,
>(
  url: string,
  method: FetchMethod = FetchMethod.Get,
  json: boolean = true,
  urlBuilder?: (params: UrlBuilderParams) => string
): FetchResult<T, K> {
  const [status, setStatus] = useState<FetchStatus>(FetchStatus.Idle)
  const [error, setError] = useState<unknown | undefined>()
  const [response, setResponse] = useState<KyResponse | undefined>()
  const [data, setData] = useState<T | undefined>()
  const [stateScope, setStateScope] = useState(url)
  const activeUrlRef = useRef(url)
  const scopeGenerationRef = useRef(0)

  if (activeUrlRef.current !== url) {
    activeUrlRef.current = url
    scopeGenerationRef.current += 1
  }

  const fetchFn = useCallback(
    async (params?: FetchFnParam<T, K>): Promise<T | undefined> => {
      if (activeUrlRef.current !== url) return undefined

      const scopeGeneration = scopeGenerationRef.current
      const isCurrentRequest = () =>
        activeUrlRef.current === url &&
        scopeGenerationRef.current === scopeGeneration &&
        !params?.signal?.aborted

      setStateScope(url)
      setStatus(FetchStatus.Loading)
      setError(undefined)
      if (!params?.preserveData) {
        setResponse(undefined)
        setData(undefined)
      }

      let fetchResp: KyResponse | undefined
      let resultData: T | undefined

      try {
        const fetchUrl =
          urlBuilder && params?.urlBuilderParams
            ? urlBuilder(params.urlBuilderParams)
            : url

        fetchResp = await ky(fetchUrl, {
          retry: params?.retry ?? 2,
          timeout: params?.timeout ?? 90000,
          mode: "cors",
          credentials: "include",
          method: method,
          searchParams: params?.searchParams,
          json: params?.jsonBody,
          body: params?.body,
          cache: params?.cache,
          signal: params?.signal,
          headers: params?.headers,
        })

        if (!isCurrentRequest()) return undefined

        setResponse(fetchResp)
        setStatus(FetchStatus.Success)
      } catch (error: unknown) {
        if (!isCurrentRequest()) return undefined

        if (error instanceof HTTPError) {
          const status = error.response.status
          switch (status) {
            case 401:
              console.error("Authentication error.", status)
              redirectToLoginForExpiredSession()
              break
            case 400:
            case 403:
            case 404:
              console.error("Client error.", status)
              break
            case 500:
              console.error("Server error.", status)
              break
            default:
              console.error("Unexpected status.", status)
          }

          const errorJson = await error.response.json().catch(() => undefined)
          if (errorJson) {
            resultData = errorJson
            setData(errorJson)
            params?.callbackFn?.(errorJson)
            ;(error as { data?: unknown }).data = errorJson
          }
          setError(error)
          setStatus(FetchStatus.Error)
        } else if (error instanceof TimeoutError) {
          console.error("Timeout error.")
          setError(error)
          setStatus(FetchStatus.Error)
        } else {
          const errorName = GetErrorName(error)
          if (errorName === "AbortError") {
            console.error("Request aborted.")
            return undefined
          }
          console.error(`Unknown error: ${error}`)
          setError(error)
          setStatus(FetchStatus.Error)
        }
        throw error
      } finally {
        if (json && fetchResp) {
          const resp = fetchResp as KyResponse
          const data = await resp.json<T>()
          resultData = data
          if (isCurrentRequest()) {
            setData(data)
            params?.callbackFn?.(data)
          }
        }
      }
      return isCurrentRequest() ? resultData : undefined
    },
    [url, method, json, urlBuilder]
  )

  const isCurrentScope = stateScope === url

  return {
    status: isCurrentScope ? status : FetchStatus.Idle,
    error: isCurrentScope ? error : undefined,
    response: isCurrentScope ? response : undefined,
    data: isCurrentScope ? data : undefined,
    fetchFn,
  }
}
